import type { FarmPositions } from "@stepfinance/farms";
import type { FarmInfoData } from "@stepfinance/farms/src/lib/info";
import { initializeFarmInfoData } from "@stepfinance/farms/src/lib/info";
import type { FC } from "react";
import React, { useCallback, useContext, useMemo, useState } from "react";
import useSWRImmutable from "swr/immutable";

import {
  useAldrinFarms,
  useAtrixFarms,
  useMercurialFarms,
  useOrcaFarms,
  useRaydiumFarms,
  useStepFarms,
} from "../hooks";
import type { StepFarmMap } from "../hooks/farms";
import { useQuarryPositionsQuery } from "../hooks/queries/farms/useQuarryPositionsQuery";
import { STEP_API_ENDPOINT, swrFetcher } from "../utils/swr";
import { useNetworkConfig } from "./app/config";

type FarmContextState = {
  refreshFarms: () => Promise<void>;
  refreshing: boolean;
  setFarmBalance: (balance: number) => void;
  farmBalance: number;
  farmInfoData: FarmInfoData;
  farmPositions: FarmPositions[];
  stepFarmsMap: StepFarmMap;
  totalPendingRewards: number;
  expected24HrRewards: number;
};

const FarmContext = React.createContext<FarmContextState>({
  refreshFarms: Promise.resolve,
  refreshing: false,
  setFarmBalance: () => {},
  farmBalance: 0,
  farmInfoData: initializeFarmInfoData(),
  farmPositions: [],
  stepFarmsMap: new Map(),
  totalPendingRewards: 0,
  expected24HrRewards: 0,
});

export const FarmProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const { network } = useNetworkConfig();

  const [farmBalance, setFarmBalance] = useState(0);

  const { data: farmData, mutate: refresh } = useSWRImmutable<FarmInfoData>(
    `${STEP_API_ENDPOINT}/v1/integration/farms?cluster=${network}`,
    swrFetcher,
  );
  const farmInfoData = farmData ?? initializeFarmInfoData();

  const { farms: stepFarmsMap, refreshStepFarms } = useStepFarms();

  const {
    atrixFarms: atrixFarmInfoDataMap,
    orcaFarms: orcaFarmInfoDataMap,
    raydiumFarms: raydiumFarmInfoDataMap,
  } = farmInfoData;

  const { data: quarryFarmsData } = useQuarryPositionsQuery();

  const raydiumFarms = useRaydiumFarms(raydiumFarmInfoDataMap);
  const orcaFarms = useOrcaFarms(orcaFarmInfoDataMap);
  const mercurialFarms = useMercurialFarms();
  const atrixFarms = useAtrixFarms(atrixFarmInfoDataMap);
  const aldrinFarms = useAldrinFarms();
  const stepFarms = Array.from(stepFarmsMap.values()).filter((f) => f.userStake !== undefined);

  const farmPositions = useMemo(() => {
    const quarryFarms = quarryFarmsData?.farmPositions ?? [];
    const positions = [
      ...raydiumFarms,
      ...orcaFarms,
      ...stepFarms,
      ...mercurialFarms,
      ...atrixFarms,
      ...aldrinFarms,
      ...quarryFarms,
    ];
    return positions;
  }, [quarryFarmsData, raydiumFarms, orcaFarms, stepFarms, mercurialFarms, atrixFarms, aldrinFarms]);

  const totalPendingRewards = useMemo(() => {
    return farmPositions.reduce((total, currentValue) => {
      const value = currentValue.rewards.reduce((a, b) => a + (b.rewardInUSD ?? 0), 0);

      return total + value;
    }, 0);
  }, [farmPositions]);

  const expected24HrRewards = useMemo(() => {
    return farmPositions.reduce((total, farm) => {
      const { apr, valueInUSD } = farm;
      const expectedReward24hr = ((apr ?? 0) / 365 / 100) * (valueInUSD ?? 0);
      return (total += expectedReward24hr);
    }, 0);
  }, [farmPositions]);

  const refreshFarms = useCallback(async () => {
    setRefreshing(true);
    refreshStepFarms();
    await refresh();
    setRefreshing(false);
  }, [refresh, refreshStepFarms]);

  return (
    <FarmContext.Provider
      value={{
        // Raw farm data
        farmInfoData,
        stepFarmsMap,
        refreshFarms,
        refreshing,
        // Farm positions
        farmPositions,
        totalPendingRewards,
        expected24HrRewards,
        setFarmBalance,
        farmBalance,
      }}
    >
      {children}
    </FarmContext.Provider>
  );
};

export const useFarms = () => useContext(FarmContext);
